import { useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { isMobileOnly } from "react-device-detect";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import isEmpty from "lodash/isEmpty";
import compact from "lodash/compact";
import orderBy from "lodash/orderBy";

import { numberFormatter, numberFormatterFull } from "../../utils/Formatters";
import { notify, notifySimple } from "../../themes/components/Notifications";
import { getLocalTime } from "../../utils/Time";
import { usePagination } from "../../hooks/usePagination";
import useSharedStyles from "../../styles/Shared";
import useButtonStyles from "../../styles/Buttons";

import AddIcon from "@mui/icons-material/Add";
import { deleteProject } from "../../api/Project";
import SearchIcon from "@mui/icons-material/Search";
import { Badge, Chip, Divider, InputAdornment, ListItemButton, Paper, Tooltip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { COLORS } from "../../themes/Base";
import ChevronDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ReplayIcon from "@mui/icons-material/Replay";
import CustomCheckbox from "../Form/CustomCheckbox";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "../../components/Icons/DeleteIcon";
import NoResultsIcon from "../../components/Icons/NoResultsIcon";
import Header from "./Header";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import StickyPagination from "../../components/StickyPagination";
import { statusOptions } from "../../constants/project";

type Props = {
  projects: Project[];
  projectManagers: { id: string; first_name: string; last_name: string }[];
  refetch: () => void;
};

interface ProjectStats extends Project {
  entrants: number;
  completed: number;
  screened_out: number;
  terminated: number;
  quota_full: number;
  incidence: number;
  conversion: number;
  loi: number;
  dropOff: number;
  quota: number;
  lastComplete: string;
  device_forensiq_term: number;
  roundtable_term: number;
  duplicate_term: number;
  client_reject_term: number;
}

const SORT_OPTIONS = [
  { value: "inserted_at:desc", label: "Created Date (Newest first)" },
  { value: "inserted_at:asc", label: "Created Date (Oldest first)" },
  { value: "name:asc", label: "Project Name (A–Z)" },
  { value: "name:desc", label: "Project Name (Z–A)" },
  { value: "status:asc", label: "Status (A–Z)" },
  { value: "status:desc", label: "Status (Z–A)" },
];

const ProjectsList: React.FC<Props> = ({ projects, projectManagers, refetch }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [itemEdit, setItemEdit] = useState<Project | null>(null);
  const [itemDelete, setItemDelete] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("inserted_at:desc");
  const [searchPhrase, setSearchPhrase] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [managersFilter, setManagersFilter] = useState<string[]>([]);
  const [confirmFilters, setConfirmFilters] = useState({
    status: [],
    managers: [],
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [perPage, setPerPage] = useState(50);
  const { classes } = useSharedStyles();
  const { classes: buttonClasses, cx } = useButtonStyles();

  const filterOpen = Boolean(filterAnchorEl);

  const projectsStats: ProjectStats[] = projects.map((project) => {
    const entrants = project.suppliers
      .map((supplier) => supplier.entrants)
      .reduce((acc, val) => acc + val, 0);
    const completed = project.suppliers
      .map((supplier) => supplier.completed)
      .reduce((acc, val) => acc + val, 0);
    const screened_out = project.suppliers
      .map((supplier) => supplier.screened_out)
      .reduce((acc, val) => acc + val, 0);
    const terminated = project.suppliers
      .map((supplier) => supplier.terminated)
      .reduce((acc, val) => acc + val, 0);
    const quota_full = project.suppliers
      .map((supplier) => supplier.quota_full)
      .reduce((acc, val) => acc + val, 0);
    const device_forensiq_term = project.suppliers
      .map((supplier) => supplier.device_forensiq_term || 0)
      .reduce((acc, val) => acc + val, 0);
    const roundtable_term = project.suppliers
      .map((supplier) => supplier.roundtable_term || 0)
      .reduce((acc, val) => acc + val, 0);
    const duplicate_term = project.suppliers
      .map((supplier) => supplier.duplicate_term || 0)
      .reduce((acc, val) => acc + val, 0);
    const client_reject_term = project.suppliers
      .map((supplier) => supplier.client_reject_term || 0)
      .reduce((acc, val) => acc + val, 0);
    const incidence = (completed ? completed / (completed + screened_out) : 0) * 100;
    const conversion = (completed ? completed / entrants : 0) * 100;
    const dropOff =
      (entrants ? (entrants - completed - quota_full - terminated - screened_out) / entrants : 0) *
      100;
    const quota = project.suppliers
      .map((supplier) => supplier.limit)
      .reduce((acc, val) => acc + val, 0);
    const lastCompleteDate = compact(
      project.suppliers.map((supplier) => supplier.last_complete_at)
    ).reduce((acc, val) => (val > acc ? val : acc), "");

    const formatShortFromNow = (dateStr: string) => {
      const raw = getLocalTime(dateStr).fromNow(); // e.g. "a few seconds ago", "2 minutes ago", "an hour ago"
      if (/second/.test(raw)) return "just now";
      let s = raw.replace(/ minutes/gi, " mins").replace(/ minute/gi, " min");
      s = s.replace(/ hours/gi, " hrs").replace(/ hour/gi, " hr");
      return s;
    };
    const lastComplete = lastCompleteDate ? formatShortFromNow(lastCompleteDate) : "never";

    return {
      ...project,
      entrants,
      completed,
      screened_out,
      terminated,
      quota_full,
      incidence,
      conversion,
      loi: 1,
      dropOff,
      quota,
      lastComplete,
      device_forensiq_term,
      roundtable_term,
      duplicate_term,
      client_reject_term,
    };
  });

  const appliedFilterCount = confirmFilters.status.length + confirmFilters.managers.length;

  const onDeleteSubmit = async (values: ProjectDeleteVars) => {
    try {
      const response = await deleteProject(values.id);
      const { messages, result } = response.projectDelete;

      if (!isEmpty(messages)) {
        notify(messages);
      } else if (result) {
        notifySimple(["Project deleted"], "success");
        refetch();
      }
    } catch (err) {
      notifySimple(["Error deleting project"], "error");
    }
  };

  const updateSearchPhrase = (newSearchPhrase: string) => {
    onPageChange(1);
    setSearchPhrase(newSearchPhrase);
  };

  const [sortField, sortOrderValue] = sortBy.split(":");
  const filteredProjectsStats = orderBy(
    projectsStats.filter(
      (ps) =>
        (confirmFilters.status.length === 0 ||
          confirmFilters.status.includes(ps.status.toLowerCase())) &&
        (confirmFilters.managers.length === 0 ||
          confirmFilters.managers.includes(ps.account_id)) &&
        ps.name.toLowerCase().indexOf(searchPhrase.toLowerCase()) !== -1
    ),
    [sortField === "name" ? (el) => el.name.toLowerCase().trim() : sortField],
    [sortOrderValue as "asc" | "desc"]
  );

  const { currentPage, offset, onPageChange } = usePagination(
    filteredProjectsStats.length,
    perPage,
    true
  );

  const handleStatusCheckbox = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const handleApplyFilters = () => {
    setConfirmFilters({
      status: statusFilter,
      managers: managersFilter,
    });
    setFilterAnchorEl(null);
  };

  const handleClearFilters = () => {
    setConfirmFilters({
      status: [],
      managers: [],
    });
    setStatusFilter([]);
    setManagersFilter([]);
    // setFilterAnchorEl(null);
  };

  const uniqueProjectOptions = useMemo(
    () =>
      Array.from(
        new Map(
          orderBy(projectsStats, [(el) => el.name.toLowerCase().trim()], ["asc"])
            .map((project) => [project.name, { label: project.name, id: project.id }])
        ).values()
      ),
    [projectsStats]
  );

  const generateFilter = (label: string, text: string, onClear: () => void) => (
    <Box
      key={text}
      display="inline-flex"
      p="2px 8px"
      alignItems="center"
      gap="4px"
      borderRadius="4px"
      bgcolor="#EAEEFF"
      color="#2039B5"
      sx={{ ml: 1, textTransform: "Capitalize" }}
    >
      {label}: {text}
      <ClearIcon
        sx={{
          fontSize: 14,
          ml: 1,
          cursor: "pointer",
          verticalAlign: "middle",
        }}
        onClick={onClear}
      />
    </Box>
  );

  return (
    <div>
      <Box className={classes.stickyHeader}>
        <Box
          display="flex"
          width={isMobileOnly ? "100%" : "auto"}
          alignItems="center"
          flexDirection={isMobileOnly ? "column" : "row"}
          justifyContent="space-between"
          marginBottom="24px"
        >
          <Typography variant="h1" sx={{ paddingBottom: 1, marginRight: 4 }}>
            Projects
          </Typography>
          <Button
            className={cx(buttonClasses.blue, buttonClasses.iconButton)}
            component={NavLink}
            to="/project/create"
            sx={{ "& .MuiSvgIcon-root": { mr: 0 } }}
          >
            <AddIcon /> Add Project
          </Button>
        </Box>
        <Box
          display="flex"
          width="100%"
          gap={2}
          alignItems={"center"}
          flexDirection={isMobileOnly ? "column" : "row"}
        >
          <Autocomplete
            sx={{ flex: 1, minWidth: 0 }}
            freeSolo
            autoSelect={false}
            options={[
              ...new Set(
                orderBy(projectsStats, [(el) => el.name.toLowerCase().trim()], ["asc"])
                  .map((project) => project.name.trim())
              ),
            ]}
            value={searchPhrase}
            onChange={(_, v) => {
              updateSearchPhrase(v || "");
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                // label="Search for Project Name or Number"
                placeholder="Search for Project Name or Number"
                // autoFocus
                onChange={(e) => updateSearchPhrase(e.target.value)}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ color: "#67676C", width: "20px" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchPhrase ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={() => updateSearchPhrase("")}
                      >
                        <ClearIcon sx={{ color: "#67676C", width: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            )}
            PaperComponent={(props) => (
              <Paper {...props} className={classes.innerBox} sx={{ fontSize: 14 }} />
            )}
          />
          <FormControl sx={{ width: isMobileOnly ? "100%" : 281 }}>
            <Select
              fullWidth
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
              }}
              renderValue={(selected) => {
                const selectedOption = SORT_OPTIONS.find((opt) => opt.value === selected);
                return selectedOption ? (
                  <span style={{ color: "#67676C" }}>{`Sort by: ${selectedOption.label}`}</span>
                ) : (
                  ""
                );
              }}
              IconComponent={(props) =>
                props.className?.includes("MuiSelect-iconOpen") ? (
                  <ChevronUpIcon sx={{ color: COLORS.iconColor }} />
                ) : (
                  <ChevronDownIcon sx={{ color: COLORS.iconColor }} />
                )
              }
              MenuProps={{
                PaperProps: {
                  className: classes.innerBox,
                },
              }}
            >
              {SORT_OPTIONS.map((option, idx) => (
                <MenuItem
                  key={`${option.value}-${idx}`}
                  value={option.value}
                  sx={{
                    fontSize: 14,
                    margin: "8px",
                    "&.Mui-selected, &:hover, &.Mui-selected:hover": {
                      backgroundColor: "#EAEAED",
                      color: "inherit",
                      borderRadius: "8px",
                    },
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            className={cx(buttonClasses.iconButton)}
            startIcon={
              <TuneIcon sx={{ color: appliedFilterCount > 0 ? "#fff" : COLORS.iconColor }} />
            }
            sx={{
              px: "16px",
              height: 40,
              color: appliedFilterCount > 0 ? "#fff" : COLORS.iconColor,
              bgcolor: appliedFilterCount > 0 ? COLORS.secondary : "#fff",
              "&:focus, &:hover": {
                bgcolor: appliedFilterCount > 0 ? COLORS.secondary : "#fff",
              },
            }}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
          >
            Filters
            {appliedFilterCount > 0 && (
              <Badge
                badgeContent={appliedFilterCount}
                color="default"
                sx={{
                  ml: "18px",
                  mr: "12px",
                  "& .MuiBadge-badge": {
                    background: "#fff",
                    color: COLORS.secondary,
                  },
                }}
              />
            )}
          </Button>
        </Box>
        {(confirmFilters.status.length > 0 || confirmFilters.managers.length > 0) && (
          <Box fontSize={14} marginTop={2}>
            Applied Filters:
            {confirmFilters.status.map((status) =>
              generateFilter("Status", status, () => {
                // Remove this status from the filter
                const newStatus = confirmFilters.status.filter((s) => s !== status);
                setConfirmFilters({ ...confirmFilters, status: newStatus });
                setStatusFilter(newStatus);
              })
            )}
            {confirmFilters.managers.map((managerId) => {
              const pm = projectManagers.find((pm) => pm.id === managerId);
              const name = pm ? `${pm.first_name} ${pm.last_name}` : managerId;
              return generateFilter("Project Manager", name, () => {
                // Remove this manager from the filter
                const newManagers = confirmFilters.managers.filter((id) => id !== managerId);
                setConfirmFilters({ ...confirmFilters, managers: newManagers });
                setManagersFilter(newManagers);
              });
            })}
          </Box>
        )}
      </Box>

      {isEmpty(filteredProjectsStats) ? (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: "column", py: 12, mt: 25 }}>
          <NoResultsIcon />
          <Typography variant="h6">No projects found with these filters</Typography>
          <Typography
            variant="subtitle2"
            align="center"
            maxWidth="486px"
            sx={{ fontSize: "16px", color: COLORS.textSecondary }}
          >
            Looks like nothing matches your current filters. Try changing or removing a few to see
            more projects
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ height: "calc(100vh - 280px)", mt: 25, overflowY: "scroll" }}>
            {filteredProjectsStats.slice(offset, offset + perPage).map((project, idx) => (
              <Box
                key={`${project.id}-${idx}`}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #E4E8D3",
                  background: "#fff",
                  boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
                  mb: 2,
                  mx: 4,
                }}
              >
                <Header
                  spacing="16px"
                  project={project}
                  refetch={refetch}
                  onEdit={(event, project) => {
                    setAnchorEl(event.currentTarget);
                    setItemEdit(project);
                  }}
                />
                <Divider />
                {/* Stats */}
                <Box className={classes.projectStatRow}>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Entrants
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {numberFormatter.format(project.entrants)}
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Completed
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {numberFormatter.format(project.completed)}
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Screened Out
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.error }}>
                      {numberFormatter.format(project.screened_out)}
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Quality Term
                    </Typography>

                    <Typography sx={{ fontSize: 20, color: COLORS.error }}>
                      {/* Breakdown */}
                      <Tooltip
                        title={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: 14 }}>
                              Device Forensiq: {numberFormatter.format(project.device_forensiq_term)}
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                              Roundtable: {numberFormatter.format(project.roundtable_term)}
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                              Duplication: {numberFormatter.format(project.duplicate_term)}
                            </Typography>
                            <Typography sx={{ fontSize: 14 }}>
                              Client Rejection: {numberFormatter.format(project.client_reject_term)}
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="bottom"
                        slotProps={{
                          tooltip: {
                            className: classes.terminatedBreakdown,
                          },
                          arrow: {
                            sx: { color: "#fff" },
                          },
                        }}
                      >
                        <span>{numberFormatter.format(project.terminated)}</span>
                      </Tooltip>
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Quota Full
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {numberFormatter.format(project.quota_full)}
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>IR</Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {Math.round(project.incidence)}%
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Conversion
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {Math.round(project.conversion)}%
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Last Completed
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {project.lastComplete}&nbsp;&nbsp;
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Drop Off
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {Math.round(project.dropOff)}%
                    </Typography>
                  </Box>
                  <Box className={classes.projectStatItem}>
                    <Typography sx={{ fontSize: 14, color: COLORS.textSecondary }}>
                      Remaining
                    </Typography>
                    <Typography sx={{ fontSize: 20, color: COLORS.textMain }}>
                      {numberFormatterFull.format(project.quota - project.completed)} /{" "}
                      {numberFormatterFull.format(project.quota)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
          {filteredProjectsStats.length > 0 && (
            <StickyPagination
              count={filteredProjectsStats.length}
              page={currentPage}
              rowsPerPage={perPage}
              onPageChange={onPageChange}
              onRowsPerPageChange={(n) => {
                setPerPage(n);
                onPageChange(1);
              }}
            />
          )}
        </>
      )
      }

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl && itemEdit)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableScrollLock
        PaperProps={{
          className: cx(classes.innerBox),
        }}
      >
        <List className={classes.dropdownMenu}>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/project/${itemEdit?.id}/view`);
            }}
            className={classes.dropdownMenuItem}
          >
            <VisibilityIcon /> View Project
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              navigate(`/project/${itemEdit?.id}/edit`);
            }}
            className={classes.dropdownMenuItem}
          >
            <EditIcon /> Edit Project
          </ListItemButton>
          <Divider />
          <ListItemButton
            onClick={() => {
              setAnchorEl(null);
              setItemDelete(itemEdit?.id || null);
            }}
            className={cx(classes.dropdownMenuItem, "red")}
          >
            <DeleteIcon /> Delete Project
          </ListItemButton>
        </List>
      </Popover>

      <Popover
        open={filterOpen}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            className: classes.innerBox,
            sx: {
              borderRadius: "16px",
              boxShadow: 3,
              p: 2,
              minWidth: 340,
              maxHeight: "550px !important",
              overflow: "hidden",
            },
          },
        }}
      >
        <Box sx={{ position: "relative", minWidth: 330, fontSize: 14, lineHeight: "20px" }}>
          <IconButton
            onClick={() => setFilterAnchorEl(null)}
            sx={{ position: "absolute", top: 6, right: 16, p: 0, color: COLORS.textMain }}
            aria-label="Close"
          >
            <ClearIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Typography variant="h6" sx={{ fontSize: 20, my: 0 }}>
            Filters
          </Typography>

          <Typography sx={{ my: 1.5, fontSize: 16 }}>Project Manager</Typography>
          <Box
            sx={{
              mb: 3,
              maxHeight: 200,
              overflowY: "scroll",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#EAEAED",
                borderRadius: "8px",
              },
            }}>
            {projectManagers.map((pm) => (
              <Box key={pm.id} sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                <CustomCheckbox
                  sx={{ py: 0 }}
                  checked={managersFilter.includes(pm.id)}
                  onChange={() => {
                    setManagersFilter((prev) =>
                      prev.includes(pm.id) ? prev.filter((id) => id !== pm.id) : [...prev, pm.id]
                    );
                  }}
                />
                <Box component="span" sx={{ fontSize: 16 }}>
                  {pm.first_name} {pm.last_name}
                </Box>
              </Box>
            ))}
          </Box>

          <Typography sx={{ my: 1.5, fontSize: 16 }}>Status</Typography>
          <Box>
            {statusOptions.map((status) => (
              <Box key={status.value} sx={{ display: "flex", alignItems: "center" }}>
                <CustomCheckbox
                  checked={statusFilter.indexOf(status.value) > -1}
                  onChange={() => handleStatusCheckbox(status.value)}
                />
                <Chip
                  label={status.label}
                  sx={{
                    background: status.bgcolor,
                    color: status.color,
                    fontSize: 16,
                  }}
                />
              </Box>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ReplayIcon />}
              className={cx(buttonClasses.iconButton, buttonClasses.iconButton)}
              onClick={handleClearFilters}
            >
              Clear All
            </Button>
            <Button
              onClick={handleApplyFilters}
              className={cx(buttonClasses.blue, buttonClasses.iconButton)}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Popover>

      <DeleteConfirmationDialog
        open={Boolean(itemDelete)}
        onCancel={() => setItemDelete(null)}
        onConfirm={() => {
          if (itemDelete) {
            void onDeleteSubmit({ id: itemDelete });
            setItemDelete(null);
          }
        }}
        title="Are you sure you want to delete this project?"
        description="Deleting this project will permanently remove all associated data. This action cannot be undone."
      />
    </div >
  );
};

export default ProjectsList;
